import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Accordion, Button, Badge, Form, Table, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axiosInstance from '../../../utils/axiosConfig';
import { useAuth } from '../../../contexts/AuthContext';
import './SparePartsPricingTable.css';

const SparePartsPricingTable = () => {
    const { hasRole } = useAuth();
    const [allParts, setAllParts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [vehicleFilter, setVehicleFilter] = useState('');
    const [search, setSearch] = useState('');
    const [vehicles, setVehicles] = useState([]);
    const [vehicleMap, setVehicleMap] = useState({});

    const fetchAllSpareParts = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (vehicleFilter) params.append('vehicle_id', vehicleFilter);

            const firstRes = await axiosInstance.get(`/spare-parts?${params}&page=1`);
            const firstResData = firstRes.data;
            const firstPage = firstResData.data || [];
            const lastPage = firstResData.meta?.last_page ?? firstResData.last_page ?? 1;

            let allData = [...firstPage];

            if (lastPage > 1) {
                const rest = await Promise.all(
                    Array.from({ length: lastPage - 1 }, (_, i) =>
                        axiosInstance.get(`/spare-parts?${params}&page=${i + 2}`).then(r => r.data.data || [])
                    )
                );
                allData = [...allData, ...rest.flat()];
            }

            setAllParts(allData);
        } catch (error) {
            toast.error('Failed to load spare parts');
        } finally {
            setLoading(false);
        }
    }, [vehicleFilter]);

    const fetchVehicles = async () => {
        try {
            const res = await axiosInstance.get('/vehicles');
            const data = res.data.data || res.data || [];
            const list = Array.isArray(data) ? data : [];
            setVehicles(list);
            const map = {};
            list.forEach(v => { map[v.vehicle_id] = v; });
            setVehicleMap(map);
        } catch (error) {
            console.error('Error fetching vehicles:', error);
        }
    };

    useEffect(() => { fetchVehicles(); }, []);
    useEffect(() => { fetchAllSpareParts(); }, [fetchAllSpareParts]);

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this spare part?')) return;
        try {
            await axiosInstance.delete(`/spare-parts/${id}`);
            toast.success('Spare part deleted successfully!');
            fetchAllSpareParts();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to delete spare part');
        }
    };

    const filtered = allParts.filter(p => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
            p.description_en?.toLowerCase().includes(q) ||
            p.description_ar?.includes(q) ||
            p.variant_id?.toLowerCase().includes(q)
        );
    });

    const grouped = {};
    filtered.forEach(part => {
        if (!grouped[part.vehicle_id]) grouped[part.vehicle_id] = {};
        if (!grouped[part.vehicle_id][part.variant_id]) grouped[part.vehicle_id][part.variant_id] = [];
        grouped[part.vehicle_id][part.variant_id].push(part);
    });

    const sortedVehicleIds = Object.keys(grouped).sort((a, b) => {
        const vA = vehicleMap[a];
        const vB = vehicleMap[b];
        const legacyA = vA?.discontinued ? 1 : 0;
        const legacyB = vB?.discontinued ? 1 : 0;
        if (legacyA !== legacyB) return legacyA - legacyB;
        return (vA?.title_en || a).localeCompare(vB?.title_en || b);
    });

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">Loading spare parts...</p>
            </div>
        );
    }

    return (
        <>
            <div className="d-flex flex-wrap gap-3 mb-4">
                <Form.Group style={{ width: '220px' }}>
                    <Form.Label>Vehicle</Form.Label>
                    <Form.Select value={vehicleFilter} onChange={e => setVehicleFilter(e.target.value)}>
                        <option value="">All Vehicles</option>
                        {vehicles.map(v => (
                            <option key={v.id} value={v.vehicle_id}>
                                {v.title_en}{v.discontinued ? ' (Legacy)' : ''}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>
                <Form.Group style={{ minWidth: '250px', flex: 1 }}>
                    <Form.Label>Search</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Search description or variant..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </Form.Group>
            </div>

            <div className="mb-2 text-muted small">
                {allParts.length} total parts across {sortedVehicleIds.length} vehicle{sortedVehicleIds.length !== 1 ? 's' : ''}
            </div>

            {sortedVehicleIds.length === 0 ? (
                <div className="text-center text-muted py-5 border rounded">No spare parts found</div>
            ) : (
                <Accordion alwaysOpen>
                    {sortedVehicleIds.map(vehicleId => {
                        const vehicle = vehicleMap[vehicleId];
                        const variantGroups = grouped[vehicleId];
                        const totalParts = Object.values(variantGroups).flat().length;
                        const isLegacy = vehicle?.discontinued;
                        const isHidden = vehicle && !vehicle.show_on_spare_parts_page;

                        return (
                            <Accordion.Item
                                key={vehicleId}
                                eventKey={vehicleId}
                                className={isLegacy ? 'spt-legacy-item' : ''}
                            >
                                <Accordion.Header>
                                    <span className="d-flex align-items-center gap-2 w-100 me-3">
                                        <strong>{vehicle?.title_en || vehicleId}</strong>
                                        {isLegacy && <Badge bg="secondary">Legacy</Badge>}
                                        {isHidden && <Badge bg="warning" text="dark">Hidden from website</Badge>}
                                        <span className="ms-auto text-muted small fw-normal">
                                            {totalParts} part{totalParts !== 1 ? 's' : ''}
                                        </span>
                                    </span>
                                </Accordion.Header>
                                <Accordion.Body className="p-0">
                                    {Object.entries(variantGroups).map(([variantId, parts]) => (
                                        <div key={variantId} className="spt-variant-section">
                                            <div className="spt-variant-header px-3 py-2 border-bottom">
                                                <span className="text-muted small">Variant:</span>
                                                <strong className="ms-1">{variantId}</strong>
                                                <Badge bg="light" text="dark" className="ms-2 border">
                                                    {parts.length}
                                                </Badge>
                                            </div>
                                            <Table responsive hover className="mb-0 text-center spt-parts-table">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th style={{ width: '40px' }}>#</th>
                                                        <th>Description (EN)</th>
                                                        <th>Description (AR)</th>
                                                        <th style={{ width: '120px' }}>Price (SAR)</th>
                                                        <th style={{ width: '120px' }}>Created By</th>
                                                        <th style={{ width: '160px' }}>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {parts.map((part, idx) => (
                                                        <tr key={part.id}>
                                                            <td>{idx + 1}</td>
                                                            <td className="text-start">{part.description_en}</td>
                                                            <td className="text-end" dir="rtl">{part.description_ar}</td>
                                                            <td>{part.price}</td>
                                                            <td>{part.creator?.name || 'N/A'}</td>
                                                            <td>
                                                                <Link
                                                                    to={`/spare-parts-management/spare-parts-pricing/edit/${part.id}`}
                                                                    className="btn btn-warning btn-sm me-1"
                                                                >
                                                                    Edit
                                                                </Link>
                                                                {hasRole('it') && (
                                                                    <Button variant="danger" size="sm" onClick={() => handleDelete(part.id)}>
                                                                        Delete
                                                                    </Button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    ))}
                                </Accordion.Body>
                            </Accordion.Item>
                        );
                    })}
                </Accordion>
            )}
        </>
    );
};

export default SparePartsPricingTable;
